import { useEffect, useState } from 'react';
import { fetchAdminUsers, patchAdminUser, type AdminUser } from '../lib/api';

const PLANS = ['', 'starter', 'smallbiz', 'pro', 'power', 'founder'];

export default function Admin() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);

  function load() {
    setLoading(true);
    setError(null);
    fetchAdminUsers()
      .then((r) => setUsers(r.data ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  if (loading) return <p className="text-gray-500">Loading…</p>;
  if (error) return <p className="text-red-600">{error}</p>;

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Users (admin)</h1>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Email</th>
              <th className="px-4 py-3 text-left font-medium">UID</th>
              <th className="px-4 py-3 text-left font-medium">Plan</th>
              <th className="px-4 py-3 text-left font-medium">Codes</th>
              <th className="px-4 py-3 text-left font-medium">Prepaid until</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map((u) =>
              editing === u.id ? (
                <EditRow
                  key={u.id}
                  user={u}
                  onCancel={() => setEditing(null)}
                  onSaved={() => {
                    setEditing(null);
                    load();
                  }}
                />
              ) : (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-700">{u.email ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{u.id}</td>
                  <td className="px-4 py-3 text-gray-700">{u.plan || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {u.codesUsed ?? 0}/{u.codeLimit ?? 0}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{formatDate(u.prepaidUntil)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setEditing(u.id)}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditRow({
  user,
  onCancel,
  onSaved,
}: {
  user: AdminUser;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [plan, setPlan] = useState(user.plan ?? '');
  const [prepaidUntil, setPrepaidUntil] = useState(toDateInput(user.prepaidUntil));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function shift(years: number) {
    const d = new Date();
    d.setFullYear(d.getFullYear() + years);
    setPrepaidUntil(d.toISOString().slice(0, 10));
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const body: { plan?: string; prepaidUntil?: string | null } = {};
      if (plan !== (user.plan ?? '')) body.plan = plan;
      const newPrepaid = prepaidUntil ? new Date(prepaidUntil + 'T00:00:00Z').toISOString() : '';
      const oldPrepaid = user.prepaidUntil ? new Date(user.prepaidUntil).toISOString() : '';
      if (newPrepaid !== oldPrepaid) body.prepaidUntil = newPrepaid;
      if (Object.keys(body).length === 0) {
        onCancel();
        return;
      }
      await patchAdminUser(user.id, body);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <tr className="bg-blue-50/40">
      <td className="px-4 py-3 text-gray-700">{user.email ?? '—'}</td>
      <td className="px-4 py-3 text-gray-500 font-mono text-xs">{user.id}</td>
      <td className="px-4 py-3">
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1 text-sm"
        >
          {PLANS.map((p) => (
            <option key={p} value={p}>
              {p || '—'}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3 text-gray-500 text-xs">limit set by plan</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={prepaidUntil}
            onChange={(e) => setPrepaidUntil(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          />
          <button onClick={() => shift(1)} className="text-xs text-blue-600 hover:underline">
            +1y
          </button>
          <button onClick={() => shift(5)} className="text-xs text-blue-600 hover:underline">
            +5y
          </button>
          <button onClick={() => setPrepaidUntil('')} className="text-xs text-gray-500 hover:underline">
            Clear
          </button>
        </div>
        {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button
          onClick={save}
          disabled={busy}
          className="text-sm text-white bg-blue-600 hover:bg-blue-700 rounded px-3 py-1 mr-2 disabled:opacity-50"
        >
          Save
        </button>
        <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-900">
          Cancel
        </button>
      </td>
    </tr>
  );
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const now = Date.now();
  const expired = d.getTime() < now;
  return d.toISOString().slice(0, 10) + (expired ? ' (expired)' : '');
}

function toDateInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
